import { Divider, Form } from 'antd';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import { DefaultRouteTabs } from '@/components/_common';
import { PageProps } from '@/types/PageProps';
import BillsToPayForm from '@/components/Reports/FinancialReports/BillsToPayForm';
import {
  ReportBillsToPayFormValues,
  ReportBillsToPayRequestQuery,
  ReportTypeReportBillsToPay,
} from '@/core/domain/reports/financial/billsToPay/BillsToPay.types';
import useCommonGenerateReport from '@/core/domain/reports/commons/Report.hook';
import useFileLoading, { FileType, FILE_FORMAT } from '@/hooks/useFileLoading';
import useReportGenerate, { ReportCallback } from '@/hooks/useReportGenerate';
import moment from 'moment';
import { useAccountsPayableReport } from '@/core/domain/questReport/Reports/AccountsPayable/AccountsPayable.hook';
import {
  QuestAccountsPayableReportType,
  QuestAccountsPayableRequest,
  QuestResponseType,
  REPORT_ACCOUNTS_PAYABLE_FILTER_DATE_BY_FORMAT,
  REPORT_ACCOUNTS_PAYABLE_REPORT_TYPE,
} from '@/core/domain/questReport/Reports/AccountsPayable/AccountsPayable.types';
import { convertParams } from '@/utils/queryParams';
import { useEffect } from 'react';

export default function FinancialBillsToPayPage({ route }: PageProps) {
  const [form] = Form.useForm<ReportBillsToPayFormValues>();
  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();
  const report = useReportGenerate();
  const watchSynthetic = Form.useWatch('synthetic', form);

  const params = convertParams<ReportBillsToPayFormValues>({
    initialDate: 'string',
    finalDate: 'string',
    companiesIds: 'array-number',
    lsSituacao: 'array-string',
    dateType: 'string',
    reportType: 'string',
  });

  useEffect(() => {
    const { initialDate, finalDate, ...rest } = params;

    if (initialDate) form.setFieldValue('initialDate', moment(initialDate));

    if (finalDate) form.setFieldValue('finalDate', moment(finalDate));

    if (rest) {
      form.setFieldsValue(rest);
    }
  }, []);

  const generateReport = useCommonGenerateReport(
    watchSynthetic
      ? '/generate/account-payable-plan-synthetic'
      : '/generate/accounts-payable',
    'Relatório gerado com sucesso!',
    'Relatório de contas a pagar gerado com sucesso.',
  );

  const questGenerateReport = useAccountsPayableReport();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const { synthetic, ...values } = await form.validateFields();

      const obj: ReportBillsToPayRequestQuery = {
        ...values,
        initialDate: moment(values.initialDate).format('YYYY-MM-DD'),
        finalDate: moment(values.finalDate).format('YYYY-MM-DD'),
        reportType:
          values.reportType === ReportTypeReportBillsToPay.accountPlans &&
          synthetic
            ? ReportTypeReportBillsToPay.accountPlansSynthetic
            : values.reportType,
        format: FILE_FORMAT[format],
      };

      const objQuest: QuestAccountsPayableRequest = {
        ...values,
        companiesIds: values.companiesIds?.toString(),
        costCenterIds: values.costCenterIds?.toString(),
        bankAccountIds: values.bankAccountIds?.toString(),
        accountsPlanIds: values.planAccountIds?.toString(),
        documentTypeIds: values.documentTypeIds?.toString(),
        employeeIds: values.employeesIds?.toString(),
        supplierIds: values.suppliersIds?.toString(),
        responseType: QuestResponseType.CSV,
        startDate: moment(values.initialDate).format('YYYY-MM-DD'),
        endDate: moment(values.finalDate).format('YYYY-MM-DD'),
        situationOpen: values.lsSituacao.includes('OPEN'),
        situationSettled: values.lsSituacao.includes('SETTLED'),
        filterDateBy:
          REPORT_ACCOUNTS_PAYABLE_FILTER_DATE_BY_FORMAT[values.dateType],
        reportType:
          values.reportType === ReportTypeReportBillsToPay.accountPlans &&
          synthetic
            ? QuestAccountsPayableReportType.planAccountSynthetic
            : REPORT_ACCOUNTS_PAYABLE_REPORT_TYPE[values.reportType],
      };

      const result =
        format === 'CSV' &&
        (values.reportType === ReportTypeReportBillsToPay.accountPlans ||
          values.reportType === ReportTypeReportBillsToPay.bankAccount)
          ? await questGenerateReport.mutateAsync(objQuest)
          : await generateReport.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleView = () => {
    handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: 'relatorio_contas_a_pagar.pdf',
        fileType: 'application/pdf',
      }),
    );
  };

  const handlePdf = () => {
    handleGenerateReport('PDF', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_contas_a_pagar.pdf',
      }),
    );
  };

  const handleCsv = () => {
    handleGenerateReport('CSV', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_contas_a_pagar.csv',
      }),
    );
  };

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={useIntl().formatMessage({ id: route.name })} />

      <BillsToPayForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        viewReport={handleView}
        generatePdf={handlePdf}
        generateCsv={handleCsv}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
}
